import { Wall, OPPOSITE_WALL } from '../types';

/**
 * Generate a maze using the Recursive Backtracker (DFS) algorithm.
 * Returns a 2D array of wall bitflags where each cell encodes its walls.
 * All outer boundary walls are always set.
 */
export function generateMaze(width: number, height: number): number[][] {
  // Initialize grid with all walls set (NORTH | EAST | SOUTH | WEST = 15)
  const cells: number[][] = [];
  for (let row = 0; row < height; row++) {
    cells[row] = [];
    for (let col = 0; col < width; col++) {
      cells[row][col] = Wall.NORTH | Wall.EAST | Wall.SOUTH | Wall.WEST;
    }
  }

  const visited: boolean[][] = [];
  for (let row = 0; row < height; row++) {
    visited[row] = new Array(width).fill(false);
  }

  const stack: [number, number][] = [];
  // Start carving from the bottom-left (player start area)
  const startRow = height - 1;
  const startCol = 0;
  visited[startRow][startCol] = true;
  stack.push([startRow, startCol]);

  const wallDirs: { wall: number; dr: number; dc: number }[] = [
    { wall: Wall.NORTH, dr: -1, dc: 0 },
    { wall: Wall.EAST, dr: 0, dc: 1 },
    { wall: Wall.SOUTH, dr: 1, dc: 0 },
    { wall: Wall.WEST, dr: 0, dc: -1 },
  ];

  while (stack.length > 0) {
    const [row, col] = stack[stack.length - 1];

    // Find unvisited neighbors
    const neighbors: { wall: number; row: number; col: number }[] = [];
    for (const { wall, dr, dc } of wallDirs) {
      const nr = row + dr;
      const nc = col + dc;
      if (nr >= 0 && nr < height && nc >= 0 && nc < width && !visited[nr][nc]) {
        neighbors.push({ wall, row: nr, col: nc });
      }
    }

    if (neighbors.length === 0) {
      stack.pop();
      continue;
    }

    // Pick random unvisited neighbor
    const next = neighbors[Math.floor(Math.random() * neighbors.length)];
    // Remove wall between current and next
    cells[row][col] &= ~next.wall;
    cells[next.row][next.col] &= ~OPPOSITE_WALL[next.wall];
    visited[next.row][next.col] = true;
    stack.push([next.row, next.col]);
  }

  return cells;
}
